import { Space } from "components/Common/Space";
import { ExerciseHeader } from "components/Common/ExerciseHeader";
import {
  useUpdateChapterStudentProgress,
  useUpdateHomeworkStudentProgress,
} from "hooks/api/student-progress";
import { useRole } from "hooks/common";
import { useParams } from "react-router-dom";
import { SentenceList } from "./SentenceList";
import {
  TChooseRightWordFormExercise,
  TChooseRightWordFormExercisePayload,
} from "types/app/exercises";
import "./ChooseRightWord.styles.scss";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { exerciseSelector, setExerciseState } from "store/features/exercises";

type TChooseRightWordProps = {
  /**
   * Chapter ID to which the exercise belongs.
   */
  chapterId: number;
  /**
   * An exercise raw data from API response.
   */
  exercise: TChooseRightWordFormExercise;
  isHomeworkExercise: boolean;
  /**
   * Lesson chapter order number with the exercise sequence number to display before the exercise condition.
   * @default null
   */
  prefix?: string | null;
};

export const ChooseRightWord: React.FC<TChooseRightWordProps> = (props) => {
  const { chapterId, exercise, prefix = null, isHomeworkExercise } = props;

  const { courseId, lessonId, studentId } = useParams();
  const { isUserStudent } = useRole();

  const { mutate: updateChapterProgress } = useUpdateChapterStudentProgress(
    courseId,
    lessonId,
    chapterId,
    studentId
  );
  const { mutate: updateHomeworkProgress } = useUpdateHomeworkStudentProgress(
    courseId,
    lessonId,
    chapterId,
    studentId
  );

  const dispatch = useAppDispatch();
  const { exercises: localExercises } = useAppSelector(exerciseSelector);

  // Use exercise from Redux store if available, otherwise fall back to prop
  const currentExercise = useMemo(() => {
    const reduxExercise = localExercises.find((ex) => ex.id === exercise.id);
    return reduxExercise || exercise;
  }, [localExercises, exercise]);

  const handleSaveExercise = (
    updatedExercise: TChooseRightWordFormExercise
  ) => {
    if (!isUserStudent || !studentId) return;

    if (isHomeworkExercise) {
      updateHomeworkProgress(updatedExercise);
    } else {
      updateChapterProgress(updatedExercise);
    }
  };

  return (
    <Space direction="vertical" size="medium" justify="start" fullWidth>
      <ExerciseHeader<TChooseRightWordFormExercisePayload>
        chapterId={chapterId}
        exercise={currentExercise}
        prefix={prefix}
      />
      <SentenceList
        sentences={currentExercise.payload.data.sentences}
        exercise={currentExercise}
        onSaveExercise={handleSaveExercise}
      />
    </Space>
  );
};
